import api from '@/services/api/principal.js'
import { storePrincipalCount } from '@/utils/principal-storage'
import { immerable } from "immer"
import { cloneDeep } from 'lodash'
import { useEffect, useState } from 'react'

export class Principal {

	[immerable] = true

	constructor(params) {
		this.id = params.id
		this.name = params.name
		this.uen = params.uen
		this.orgId = params?.orgId
		this.organisation = params?.organisation
		this.createdBy = params?.createdBy
		this.modifiedBy = params?.modifiedBy
		this.created = params?.created
		this.modified = params?.modified
		this.enrolmentId = params?.enrolmentId
		this.subscriptionId = params?.subscriptionId
		this.enrolmentPrincipals = params?.enrolmentPrincipals
		this.subscriptionPrincipals = params?.subscriptionPrincipals
		this['$selected'] = params?.['$selected'];
		this['$disabled'] = params?.['$disabled'];
	}
}

const usePrincipals = (
	{
		cacheFetchedItem: {
			identifier = false, //'id',
			keys = [], //['$selected'],
		}
	} = { cacheFetchedItem: { identifier: false, keys: [] } }
) => {

	const [items, setItems] = useState([])
	const [fetchedItems, setFetchedItems] = useState([])
	const [meta, setMeta] = useState(null)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState()

	async function getEditEnrollmentPrincipal(parameter) {
		setLoading(true)
		const params = parameter ?? "";
		const [errorResponse, data] = await api.getEditEnrollmentPrincipal({ params });
		setLoading(false)

		if (errorResponse) {
			return setError(errorResponse)
		}

		const { items, meta } = data;

		if (!Array.isArray(items)) {
			return setError('Principal is not an array')
		}

		const serializedData = items?.map(entry => serialize(entry))
		const newItems = serializedData.map(entry => new Principal(entry))


		setItems(newItems)
		setMeta(meta)
	}

	async function getSubscriptionPrincipal(parameter) {
		setLoading(true)
		const params = parameter ?? "";
		const [errorResponse, data] = await api.getSubscriptionPrincipal({ params });
		setLoading(false)

		if (errorResponse) {
			return setError(errorResponse)
		}

		const { items, meta } = data;

		if (!Array.isArray(items)) {
			return setError('Principal is not an array')
		}

		const serializedData = items?.map(entry => serialize(entry))
		const newItems = serializedData.map(entry => new Principal(entry))


		setItems(newItems)
		setMeta(meta)
	}

	async function getUniqueEnrollmentPrincipal(parameter) {
		setLoading(true)
		const params = parameter ?? "";
		const [errorResponse, data] = await api.getSubscriptionPrincipal({params});
		setLoading(false)

		if (errorResponse) {
			return setError(errorResponse)
		}

		const { items, meta } = data;

		if (!Array.isArray(items)) {
			return setError('Principal is not an array')
		}

		const serializedData = items?.map(entry => serialize(entry))
		const newItems = serializedData.map(entry => new Principal(entry))

		mapFetchedItems(newItems);
		setItems(newItems)
		setMeta(meta)
	}

	useEffect(() => {
		if (!items?.length || !identifier || !keys?.length) {
			return;
		}

		const concatPrincipals = [...fetchedItems];
		const copyItems = cloneDeep(items);

		for (let item of copyItems) {
			const i = concatPrincipals.findIndex(({ [identifier]: id }) => id === item?.[identifier])
			if (i > -1) {
				for (let key of keys) {
					concatPrincipals[i][key] = item?.[key] ?? concatPrincipals[i][key];
				}
			} else {
				concatPrincipals.push(item)
			}
		}

		setFetchedItems(concatPrincipals);
	}, [items])

	function mapFetchedItems(newItems) {
		if (!fetchedItems?.length || !identifier || !keys?.length || !newItems?.length) {
			return;
		}

		for (let item of newItems) {
			const i = fetchedItems.findIndex(({ [identifier]: id }) => id === item?.[identifier]);
			if (i > -1) {
				for (let key of keys) {
					item[key] = fetchedItems[i][key];
				}
			}
		}
	}

	async function fetch(parameter) {
		setLoading(true)
		const params = parameter ?? "";

		const [errorResponse, data] = await api.index({ params });

		setLoading(false)

		if (errorResponse) {
			return setError(errorResponse)
		}

		const { items, meta } = data;

		if (!Array.isArray(items)) {
			return setError('Principal is not an array')
		}

		const serializedData = items?.map(entry => serialize(entry))
		const newItems = serializedData.map(entry => new Principal(entry))

		mapFetchedItems(newItems);

		setItems(newItems)
		setMeta(meta)
		storePrincipalCount(meta?.count ?? items?.length ?? 0)
	}

	async function find(parameter) {
		setLoading(true)
		const params = parameter ?? "";

		const [errorResponse, data] = await api.index({ params });


		setLoading(false)

		if (errorResponse) {
			return setError(errorResponse)
		}

		if (!Array.isArray(data.items)) {
			return setError('Principal is not an array')
		}

		const serializedData = data.items.map(entry => serialize(entry))
		storePrincipalCount(serializedData?.length)
		return serializedData.map(entry => new Principal(entry))
	}

	async function create(parameter) {
		if (parameter && ((Array.isArray(parameter) && parameter.length === 0) || (Object.keys(parameter).length === 0))) {
			return setError('Principal is empty')
		}

		setLoading(true);

		const [errorResponse, data] = await api.create(parameter);

		setLoading(false)

		if (errorResponse) {
			setError(errorResponse)
		}

		return data;
	}

	async function deleteOne(id) {
		if (!id) {
			return setError('Principal ID not found')
		}

		setLoading(true);

		const [errorResponse, data] = await api.remove(id);

		setLoading(false)
		storePrincipalCount(0)

		if (errorResponse) {
			setError(errorResponse)
		}

		return data;
	}

	async function deleteMany(params) {
		if (!params) {
			return setError('Principal ID not found')
		}

		params = Array.isArray(params) ? params : [params]

		setLoading(true);

		const [errorResponse, data] = await api.delete({"ids": params});

		setLoading(false)
		storePrincipalCount(0)

		if (errorResponse) {
			setError(errorResponse)
		}

		return data;
	}

	function serialize(entry) {
		return {
			id: entry.principalId,
			name: entry.name,
			uen: entry.uen,
			orgId: entry?.orgId,
			organisation: entry?.organisation,
			createdBy: entry?.createdBy,
			modifiedBy: entry?.modifiedBy,
			created: entry?.created,
			modified: entry?.modified,
			enrolmentId: entry?.enrolmentId,
			subscriptionId: entry?.subscriptionId,
			enrolmentPrincipals: entry?.enrolmentPrincipals,
			subscriptionPrincipals: entry?.subscriptionPrincipals,
		}
	}


	return {
		error,
		getEditEnrollmentPrincipal,
		getUniqueEnrollmentPrincipal,
		getSubscriptionPrincipal,
		fetch,
		find,
		items,
		loading,
		setItems,
		fetchedItems,
		setFetchedItems,
		create,
		meta,
		deleteOne,
		deleteMany,
	}

}

export default usePrincipals
